import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API, apiError } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import Wordmark from "@/components/Wordmark";
import { User, LogOut, ShieldCheck, ArrowLeft, RefreshCw, AlertTriangle, CheckCircle2, FileText, Download } from "lucide-react";

const CT_KEY = "asg_customer_token";
const CU_KEY = "asg_customer";

async function customerApi(path, opts = {}) {
  const token = localStorage.getItem(CT_KEY);
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API}${path}`, { ...opts, headers });
  const contentType = res.headers.get("content-type") || "";
  const body = contentType.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    const detail = (body && body.detail) || body || "Hata oluştu";
    throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  }
  return body;
}

export default function CustomerPortalPage() {
  const { settings } = useSettings();
  const nav = useNavigate();
  const [customer, setCustomer] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CU_KEY) || "null"); } catch { return null; }
  });
  const [tc, setTc] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [renewOpen, setRenewOpen] = useState(null);
  const [notes, setNotes] = useState("");

  const load = async () => {
    if (!customer) return;
    try {
      const data = await customerApi("/customer/policies");
      setPolicies(data);
    } catch (e) {
      if (String(e.message).toLowerCase().includes("oturum") || String(e.message).toLowerCase().includes("yetkisiz")) {
        logout();
      } else {
        toast.error(e.message);
      }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customer?.tc_kimlik]);

  const login = async (e) => {
    e.preventDefault();
    if (!tc || !phone) { toast.error("T.C. ve telefon gerekli"); return; }
    setLoading(true);
    try {
      const data = await customerApi("/customer/login", {
        method: "POST",
        body: JSON.stringify({ tc_kimlik: tc, telefon: phone }),
      });
      localStorage.setItem(CT_KEY, data.token);
      localStorage.setItem(CU_KEY, JSON.stringify(data.customer));
      setCustomer(data.customer);
      toast.success("Giriş başarılı");
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const logout = () => {
    localStorage.removeItem(CT_KEY);
    localStorage.removeItem(CU_KEY);
    setCustomer(null);
    setPolicies([]);
  };

  const submitRenewal = async () => {
    try {
      await customerApi("/customer/renewal", {
        method: "POST",
        body: JSON.stringify({ policy_id: renewOpen.id, notes }),
      });
      toast.success("Yenileme talebiniz alındı, danışmanınız sizi arayacaktır");
      setRenewOpen(null);
      setNotes("");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const downloadCustomerPdf = async (policyId, fname) => {
    try {
      const token = localStorage.getItem(CT_KEY);
      const res = await fetch(`${API}/customer/policies/${policyId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let msg = "PDF alınamadı";
        try { const j = await res.json(); msg = j.detail || msg; } catch {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname || "police.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF indirildi");
    } catch (e) { toast.error(e.message); }
  };

  if (!customer) {
    return (
      <div className="min-h-screen silver-sheen flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <Wordmark size="lg" testid="customer-login-wordmark" />
          </div>
          <div className="asg-card rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.2), rgba(183,110,121,0.1))" }}>
                <User className="w-5 h-5" style={{ color: "var(--asg-primary-dark)" }} />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-semibold">Müşteri Girişi</h1>
                <p className="text-xs text-black/60">Poliçelerinizi görüntülemek için giriş yapın</p>
              </div>
            </div>
            <form onSubmit={login} className="grid gap-5">
              <div className="grid gap-2">
                <Label className="text-black">T.C. Kimlik No</Label>
                <Input value={tc} onChange={(e) => setTc(e.target.value)} required autoFocus
                  className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] text-black h-11"
                  data-testid="customer-tc" />
              </div>
              <div className="grid gap-2">
                <Label className="text-black">Telefon Numarası</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="05XX XXX XX XX"
                  className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] text-black h-11"
                  data-testid="customer-phone" />
              </div>
              <Button type="submit" disabled={loading} className="asg-btn-primary rounded-full h-11 border-0 font-semibold" data-testid="customer-login-submit">
                {loading ? "Giriş..." : "Giriş Yap"}
              </Button>
            </form>
            <div className="mt-6 pt-6 border-t border-[color:var(--asg-border)] text-center text-xs text-black/60">
              Poliçe bilgileriniz sistemde kayıtlıysa T.C. ve telefonunuzla giriş yapabilirsiniz.
            </div>
          </div>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-black/70 hover:text-black justify-center w-full" data-testid="customer-back-home">
            <ArrowLeft className="w-4 h-4" /> Ana Sayfaya Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--asg-bg)" }}>
      <header className="glass sticky top-0 z-30">
        <div className="max-w-6xl mx-auto h-16 px-5 lg:px-8 flex items-center justify-between">
          <Wordmark size="md" testid="customer-portal-wordmark" />
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-sm text-white">{customer.name || "Müşteri"}</span>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">TC: {customer.tc_kimlik.slice(0,3)}****{customer.tc_kimlik.slice(-3)}</span>
            </div>
            <button onClick={logout} className="text-zinc-400 hover:text-white" data-testid="customer-logout" title="Çıkış">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] asg-primary font-semibold">Müşteri Portalı</span>
            <h1 className="font-heading text-3xl sm:text-4xl mt-2">Poliçelerim</h1>
          </div>
          <Button onClick={load} variant="outline" className="border-white/10 bg-transparent text-white hover:bg-white/5 rounded-full">
            <RefreshCw className="w-4 h-4 mr-2" /> Yenile
          </Button>
        </div>

        {policies.length === 0 && (
          <div className="asg-card rounded-2xl p-12 text-center text-zinc-400" data-testid="customer-no-policies">
            Henüz kayıtlı poliçeniz bulunamadı.
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-5">
          {policies.map((p) => {
            const cancelled = !!p.is_cancelled;
            const dl = p.days_left;
            const expiring = !cancelled && dl !== null && dl <= 30;
            const criticalExpiry = !cancelled && dl !== null && dl <= 3;
            return (
              <div key={p.id} className="asg-card rounded-2xl p-6" data-testid={`customer-policy-${p.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-zinc-500">{p.product_title}</div>
                    <div className="font-heading text-2xl mt-1 font-mono">{p.policy_number}</div>
                  </div>
                  {cancelled ? (
                    <span className="text-[10px] uppercase tracking-widest bg-zinc-500/20 text-zinc-300 border border-zinc-500/30 rounded-full px-3 py-1">İptal</span>
                  ) : criticalExpiry ? (
                    <span className="text-[10px] uppercase tracking-widest bg-red-500/20 text-red-300 border border-red-500/30 rounded-full px-3 py-1">Süresi Bitiyor</span>
                  ) : expiring ? (
                    <span className="text-[10px] uppercase tracking-widest bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-full px-3 py-1">Yenileme Zamanı</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded-full px-3 py-1">Aktif</span>
                  )}
                </div>
                <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-xs text-zinc-500">Başlangıç</div>
                    <div className="text-white">{p.start_date}</div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500">Bitiş</div>
                    <div className="text-white">{p.end_date}</div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500">Kalan Süre</div>
                    <div className={cancelled ? "text-zinc-500" : criticalExpiry ? "text-red-400" : expiring ? "text-amber-400" : "text-emerald-400"}>
                      {cancelled ? "—" : dl === null ? "—" : dl < 0 ? `${Math.abs(dl)} gün önce bitti` : `${dl} gün`}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500">Prim</div>
                    <div className="text-white">{p.premium ? `${p.premium} ₺` : "—"}</div>
                  </div>
                </div>
                {cancelled && p.cancellation_reason && (
                  <div className="mt-4 rounded-lg bg-white/5 border border-white/10 p-3 text-xs text-zinc-400" data-testid={`customer-policy-cancel-reason-${p.id}`}>
                    <span className="text-zinc-500">İptal Nedeni:</span> {p.cancellation_reason}
                  </div>
                )}
                <div className="mt-5 flex flex-wrap gap-2">
                  {p.pdf_name && (
                    <Button onClick={() => downloadCustomerPdf(p.id, p.pdf_name)}
                      variant="outline"
                      className="rounded-full h-9 text-xs border-[color:var(--asg-border)] text-black hover:bg-[color:var(--asg-surface)]"
                      data-testid={`customer-pdf-download-${p.id}`}>
                      <Download className="w-3.5 h-3.5 mr-1.5" /> Poliçe PDF İndir
                    </Button>
                  )}
                  {!cancelled && !p.renewal_request_id && (
                    <Button onClick={() => setRenewOpen(p)} className="asg-btn-primary rounded-full h-9 text-xs border-0" data-testid={`customer-renew-${p.id}`}>
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Yenileme Talebi Aç
                    </Button>
                  )}
                  {p.renewal_request_id && (
                    <div className="inline-flex items-center gap-1.5 text-xs text-emerald-700">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Yenileme talebiniz alındı
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>

      <Dialog open={!!renewOpen} onOpenChange={(o) => !o && setRenewOpen(null)}>
        <DialogContent className="bg-[color:var(--asg-surface)] border-white/10 text-white max-w-lg" data-testid="customer-renew-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Yenileme Talebi</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {renewOpen?.product_title} — {renewOpen?.policy_number} için yenileme talebi açıyorsunuz. Danışmanınız kısa süre içinde sizinle iletişime geçecek.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <Label>Ek Not (isteğe bağlı)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Poliçe hakkında iletmek istediğiniz notlar" className="bg-[color:var(--asg-bg)] border-white/10" data-testid="customer-renew-notes" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenewOpen(null)} className="text-zinc-300 hover:text-white">Vazgeç</Button>
            <Button onClick={submitRenewal} className="asg-btn-primary rounded-full border-0" data-testid="customer-renew-submit">Talebi Gönder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
